import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormGroup,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Radio,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { formatScreeningLabel } from '../utils/screeningLabel';
import type {
  IrisCollectionDraftV1,
  IrisBornEarlyAnswer,
  IrisCommunicationLevel,
  IrisDiagnosisStatus,
  IrisLastSlept,
  IrisParentIntakeV1,
  IrisVisionConcernAnswer,
} from '../types';
import { resetClientRunId } from '../lib/clientRun';
import { productTokens } from '../theme';

interface TaskResult {
  task: string;
  label: string;
  confidence: number;
  prob?: number;
  threshold?: number;
}

interface DiagnosisResult {
  id?: number;
  name?: string;
  label?: string;
  confidence?: number | null;
  tasks?: TaskResult[];
}

interface RunnerDoneMessage {
  type: 'iris:test:done';
  result?: DiagnosisResult;
}

const DEBUG_ENABLED = process.env.NODE_ENV !== 'production';

const logDebug = (message: string, payload?: unknown) => {
  if (!DEBUG_ENABLED) return;
  if (payload !== undefined) {
    console.debug(message, payload);
  } else {
    console.debug(message);
  }
};

const sanitizeDiagnosis = (value?: DiagnosisResult | null): DiagnosisResult | null => {
  if (!value) return null;
  const tasks = Array.isArray(value.tasks) ? value.tasks : undefined;
  return { ...value, tasks };
};

dayjs.locale('zh-cn');

export default function TestLauncher(): JSX.Element {
  const nav = useNavigate();
  const winRef = useRef<Window | null>(null);
  const closeAttempts = useRef(0);

  const [status, setStatus] = useState<'idle' | 'opened' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiagnosisResult | null>(null);

  const [childClinicId, setChildClinicId] = useState('');
  const [childName, setChildName] = useState('');
  const [officialDiagnosisStatus, setOfficialDiagnosisStatus] = useState<IrisDiagnosisStatus | ''>(
    '',
  );
  const [diagnosedOn, setDiagnosedOn] = useState('');
  const [clinicianName, setClinicianName] = useState('');
  const [diagnosisInstitution, setDiagnosisInstitution] = useState('');
  const [dob, setDob] = useState<string>('');
  const [bornEarly, setBornEarly] = useState<IrisBornEarlyAnswer | ''>('');
  const [weeksEarly, setWeeksEarly] = useState<number | ''>('');
  const [visionConcerns, setVisionConcerns] = useState<IrisVisionConcernAnswer | ''>('');
  const [wearsGlasses, setWearsGlasses] = useState(false);
  const [eyeTurn, setEyeTurn] = useState(false);
  const [visionOtherEnabled, setVisionOtherEnabled] = useState(false);
  const [visionOther, setVisionOther] = useState('');
  const [communication, setCommunication] = useState<IrisCommunicationLevel | ''>('');
  const [lastSlept, setLastSlept] = useState<IrisLastSlept | ''>('');
  const [dobPickerOpen, setDobPickerOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    childClinicId?: string;
    officialDiagnosisStatus?: string;
    dob?: string;
    bornEarly?: string;
    weeksEarly?: string;
    visionConcerns?: string;
    communication?: string;
    lastSlept?: string;
  }>({});

  const normalizeDobInput = useCallback((value: string): string => {
    const raw = value.trim().replace(/[\\/]/g, '-');
    if (raw.includes('-')) {
      const parts = raw.split('-').slice(0, 3);
      const year = (parts[0] ?? '').replace(/\D/g, '').slice(0, 4);
      const month = (parts[1] ?? '').replace(/\D/g, '').slice(0, 2);
      const day = (parts[2] ?? '').replace(/\D/g, '').slice(0, 2);

      let out = year;
      if (parts.length > 1) out += `-${month}`;
      if (parts.length > 2) out += `-${day}`;
      return out;
    }

    const digits = raw.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 4) return digits;
    const year = digits.slice(0, 4);
    const month = digits.slice(4, 6);
    if (digits.length <= 6) return `${year}-${month}`;
    const day = digits.slice(6, 8);
    return `${year}-${month}-${day}`;
  }, []);

  const parseDob = useCallback(
    (isoDate: string): { year: number; month: number; day: number } | null => {
      const trimmed = isoDate.trim();
      const m = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/.exec(trimmed);
      if (!m) return null;
      const year = Number(m[1]);
      const month = Number(m[2]);
      const day = Number(m[3]);
      if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;

      const dobDate = new Date(year, month - 1, day);
      if (Number.isNaN(dobDate.getTime())) return null;
      if (
        dobDate.getFullYear() !== year ||
        dobDate.getMonth() !== month - 1 ||
        dobDate.getDate() !== day
      ) {
        return null;
      }
      return { year, month, day };
    },
    [],
  );

  const calcAgeMonths = useCallback(
    (isoDate: string): number | null => {
      const parts = parseDob(isoDate);
      if (!parts) return null;
      const { year, month, day } = parts;

      const dobDate = new Date(year, month - 1, day);
      const now = new Date();
      if (now.getTime() < dobDate.getTime()) return null;

      let months =
        (now.getFullYear() - dobDate.getFullYear()) * 12 + (now.getMonth() - dobDate.getMonth());
      if (now.getDate() < dobDate.getDate()) months -= 1;
      return Math.max(0, months);
    },
    [parseDob],
  );

  const ageMonths = useMemo(() => (dob ? calcAgeMonths(dob) : null), [calcAgeMonths, dob]);

  const dobPickerValue = useMemo<Dayjs | null>(() => {
    if (!dob) return null;
    const parsed = parseDob(dob);
    if (!parsed) return null;
    const dt = dayjs(new Date(parsed.year, parsed.month - 1, parsed.day));
    return dt.isValid() ? dt : null;
  }, [dob, parseDob]);

  const persistResult = useCallback((value: DiagnosisResult | null) => {
    try {
      if (!value) {
        localStorage.removeItem('iris:last_result');
        localStorage.removeItem('argus:last_result');
        return;
      }
      const sanitized = sanitizeDiagnosis(value);
      localStorage.setItem('iris:last_result', JSON.stringify(sanitized));
      localStorage.removeItem('argus:last_result');
    } catch (persistErr) {
      logDebug('[Launcher] failed to persist result', persistErr);
    }
  }, []);

  const closePopup = useCallback((label: string) => {
    const handle = winRef.current;
    if (!handle || handle.closed) {
      return;
    }
    try {
      handle.close();
      logDebug(label);
    } catch (closeErr) {
      logDebug(`${label} (failed)`, closeErr);
    }
  }, []);

  const closePopupRef = useRef(closePopup);

  useEffect(() => {
    closePopupRef.current = closePopup;
  }, [closePopup]);

  const getStoredStaticName = useCallback((): string | undefined => {
    try {
      const collectionRaw = localStorage.getItem('iris_collection_context');
      if (collectionRaw) {
        const parsed = JSON.parse(collectionRaw) as IrisCollectionDraftV1;
        const value = parsed?.child?.name;
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.length > 0) return trimmed;
        }
      }
      const raw = localStorage.getItem('iris_static') ?? localStorage.getItem('argus_static');
      if (!raw) return undefined;
      if (!localStorage.getItem('iris_static') && localStorage.getItem('argus_static')) {
        localStorage.setItem('iris_static', raw);
        localStorage.removeItem('argus_static');
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return undefined;
      const value = (parsed as Record<string, unknown>)['name'];
      if (typeof value !== 'string') return undefined;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    } catch (readErr) {
      logDebug('[Launcher] failed to read stored name', readErr);
      return undefined;
    }
  }, []);

  useEffect(() => {
    try {
      const collectionRaw = localStorage.getItem('iris_collection_context');
      if (collectionRaw) {
        const parsed = JSON.parse(collectionRaw) as IrisCollectionDraftV1;
        if (parsed?.version === 1) {
          if (typeof parsed.child?.clinicChildId === 'string') {
            setChildClinicId(parsed.child.clinicChildId);
          }
          if (typeof parsed.child?.name === 'string') {
            setChildName(parsed.child.name);
          }
          if (parsed.officialDiagnosis?.status) {
            setOfficialDiagnosisStatus(parsed.officialDiagnosis.status);
          }
          if (typeof parsed.officialDiagnosis?.diagnosedOn === 'string') {
            setDiagnosedOn(parsed.officialDiagnosis.diagnosedOn);
          }
          if (typeof parsed.officialDiagnosis?.clinicianName === 'string') {
            setClinicianName(parsed.officialDiagnosis.clinicianName);
          }
          if (typeof parsed.officialDiagnosis?.institution === 'string') {
            setDiagnosisInstitution(parsed.officialDiagnosis.institution);
          }
        }
      }

      const raw = localStorage.getItem('iris_static') ?? localStorage.getItem('argus_static');
      if (!raw) return;
      if (!localStorage.getItem('iris_static') && localStorage.getItem('argus_static')) {
        localStorage.setItem('iris_static', raw);
        localStorage.removeItem('argus_static');
      }
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      const obj = parsed as Record<string, unknown>;
      if (obj['version'] === 1 && typeof obj['dob'] === 'string') {
        setDob(normalizeDobInput(String(obj['dob'])));
        const born = obj['bornEarly'];
        if (born === 'no' || born === 'yes' || born === 'unsure') setBornEarly(born);
        const weeks = obj['weeksEarly'];
        if (typeof weeks === 'number' && Number.isFinite(weeks)) setWeeksEarly(weeks);
        const vision = obj['visionConcerns'];
        if (vision === 'no' || vision === 'yes' || vision === 'unsure') setVisionConcerns(vision);
        const details = obj['visionDetails'];
        if (details && typeof details === 'object') {
          const d = details as Record<string, unknown>;
          setWearsGlasses(Boolean(d['wearsGlasses']));
          setEyeTurn(Boolean(d['eyeTurn']));
          const other = typeof d['other'] === 'string' ? d['other'] : '';
          const trimmedOther = other.trim();
          if (trimmedOther.length > 0) {
            setVisionOtherEnabled(true);
            setVisionOther(trimmedOther);
          }
        }
        const comm = obj['communication'];
        if (
          comm === 'no_words' ||
          comm === 'few_single_words' ||
          comm === 'many_single_words' ||
          comm === 'two_word_phrases_or_more'
        ) {
          setCommunication(comm);
        }
        const sleep = obj['lastSlept'];
        if (sleep === 'lt2h' || sleep === '2to6h' || sleep === 'gt6h') setLastSlept(sleep);
      }
    } catch (loadErr) {
      logDebug('[Launcher] failed to load cached static info', loadErr);
    }
  }, [normalizeDobInput]);

  const completeRun = useCallback(
    (payload?: DiagnosisResult) => {
      setStatus('done');
      const fallbackName = getStoredStaticName();
      const merged = payload
        ? (() => {
            const resolvedName = payload.name ?? fallbackName;
            return resolvedName ? { ...payload, name: resolvedName } : payload;
          })()
        : undefined;
      const enriched = sanitizeDiagnosis(merged);
      if (enriched) {
        setResult(enriched);
        persistResult(enriched);
      } else {
        setResult(null);
        persistResult(null);
      }
      closePopupRef.current?.('[Launcher] closed popup after completion');
      setTimeout(() => {
        const targetPath = enriched?.id ? `/results/${enriched.id}` : '/dashboard';
        logDebug('[Launcher] navigate after completion', targetPath);
        nav(targetPath, { replace: true });
      }, 200);
    },
    [getStoredStaticName, nav, persistResult],
  );

  useEffect(() => {
    localStorage.removeItem('iris:test:done');
    localStorage.removeItem('argus:test:done');
    const channel = new BroadcastChannel('iris-runner');

    const onMessage = (event: MessageEvent<RunnerDoneMessage>) => {
      logDebug('[Launcher] broadcast message', event.data);
      if (event.data?.type === 'iris:test:done') {
        completeRun(event.data.result ?? undefined);
      }
    };

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [completeRun]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'iris:test:done' || event.key === 'argus:test:done') {
        logDebug('[Launcher] storage completion event');
        try {
          const raw =
            localStorage.getItem('iris:last_result') ?? localStorage.getItem('argus:last_result');
          const parsed = raw ? (JSON.parse(raw) as DiagnosisResult) : null;
          completeRun(sanitizeDiagnosis(parsed) ?? undefined);
        } catch (parseErr) {
          logDebug('[Launcher] failed to read stored result', parseErr);
          completeRun();
        }
      }

      if (event.key === 'iris:please-close' || event.key === 'argus:please-close') {
        closeAttempts.current = 0;
        const retryClose = () => {
          closeAttempts.current += 1;
          closePopupRef.current?.(
            `[Launcher] storage retry close attempt ${closeAttempts.current}`,
          );
          if (winRef.current && !winRef.current.closed && closeAttempts.current < 15) {
            window.setTimeout(retryClose, 100);
          }
        };
        retryClose();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [closePopup, completeRun]);

  const openRunner = useCallback(() => {
    setError(null);
    setStatus('idle');
    setFieldErrors({});

    const nextErrors: {
      childClinicId?: string;
      officialDiagnosisStatus?: string;
      dob?: string;
      bornEarly?: string;
      weeksEarly?: string;
      visionConcerns?: string;
      communication?: string;
      lastSlept?: string;
    } = {};

    if (!childClinicId.trim()) {
      nextErrors.childClinicId = '请输入院内儿童编号。';
    }
    if (!officialDiagnosisStatus) {
      nextErrors.officialDiagnosisStatus = '请选择参考 ASD 状态。';
    }
    const resolvedAgeMonths = ageMonths;
    if (!dob || resolvedAgeMonths === null) {
      nextErrors.dob = '请输入有效的出生日期。';
    }
    if (!bornEarly) {
      nextErrors.bornEarly = '请选择一项。';
    }
    if (bornEarly === 'yes') {
      const resolvedWeeks = typeof weeksEarly === 'number' ? weeksEarly : Number(weeksEarly);
      if (!Number.isFinite(resolvedWeeks) || resolvedWeeks <= 0 || resolvedWeeks > 40) {
        nextErrors.weeksEarly = '请输入早产周数（1–40）。';
      }
    }
    if (!visionConcerns) {
      nextErrors.visionConcerns = '请选择一项。';
    }
    if (!communication) {
      nextErrors.communication = '请选择一项。';
    }
    if (!lastSlept) {
      nextErrors.lastSlept = '请选择一项。';
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      const dobParts = parseDob(dob.trim());
      const paddedDob = dobParts
        ? `${dobParts.year}-${String(dobParts.month).padStart(2, '0')}-${String(dobParts.day).padStart(2, '0')}`
        : dob.trim();
      const resolvedWeeks = typeof weeksEarly === 'number' ? weeksEarly : Number(weeksEarly);
      const intake: IrisParentIntakeV1 = {
        version: 1,
        dob: paddedDob,
        ageMonths: resolvedAgeMonths ?? 0,
        bornEarly: bornEarly as IrisBornEarlyAnswer,
        weeksEarly: bornEarly === 'yes' && Number.isFinite(resolvedWeeks) ? resolvedWeeks : null,
        visionConcerns: visionConcerns as IrisVisionConcernAnswer,
        visionDetails:
          visionConcerns === 'yes'
            ? {
                wearsGlasses,
                eyeTurn,
                other:
                  visionOtherEnabled && visionOther.trim().length > 0
                    ? visionOther.trim()
                    : undefined,
              }
            : null,
        communication: communication as IrisCommunicationLevel,
        lastSlept: lastSlept as IrisLastSlept,
      };
      const browserNavigator = navigator as Navigator & { deviceMemory?: number };
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const clientRunId = resetClientRunId();
      const collectionContext: IrisCollectionDraftV1 = {
        version: 1,
        child: {
          clinicChildId: childClinicId.trim(),
          name: childName.trim() || undefined,
          dateOfBirth: paddedDob,
        },
        officialDiagnosis: {
          status: officialDiagnosisStatus as IrisDiagnosisStatus,
          diagnosedOn: diagnosedOn.trim() || null,
          clinicianName: clinicianName.trim() || undefined,
          institution: diagnosisInstitution.trim() || undefined,
        },
        intake,
        collectionMeta: {
          clientRunId,
          launchedAt: new Date().toISOString(),
          launcherOrigin: window.location.origin,
          launcherPath: window.location.pathname,
          ...(timeZone ? { timeZone } : {}),
          ...(navigator.language ? { locale: navigator.language } : {}),
          ...(navigator.userAgent ? { userAgent: navigator.userAgent } : {}),
          ...(browserNavigator.platform ? { platform: browserNavigator.platform } : {}),
          ...(window.innerWidth > 0 ? { viewportWidth: window.innerWidth } : {}),
          ...(window.innerHeight > 0 ? { viewportHeight: window.innerHeight } : {}),
          ...(window.devicePixelRatio > 0 ? { pixelRatio: window.devicePixelRatio } : {}),
        },
      };

      localStorage.setItem('iris_static', JSON.stringify(intake));
      localStorage.setItem('iris_collection_context', JSON.stringify(collectionContext));
      localStorage.removeItem('argus_static');
    } catch (persistErr) {
      logDebug('[Launcher] failed to persist static payload', persistErr);
    }

    const url = `${window.location.origin}/runner/start`;
    const windowFeatures =
      'popup=yes,width=1280,height=800,toolbar=0,menubar=0,location=0,status=0,noopener=no,noreferrer=no';

    const handle = window.open(url, 'IrisTestRunner', windowFeatures);
    if (!handle) {
      setError('弹窗被拦截。请允许本网站的弹出窗口。');
      return;
    }

    winRef.current = handle;
    setStatus('opened');
  }, [
    childClinicId,
    childName,
    officialDiagnosisStatus,
    diagnosedOn,
    clinicianName,
    diagnosisInstitution,
    ageMonths,
    dob,
    parseDob,
    bornEarly,
    weeksEarly,
    visionConcerns,
    wearsGlasses,
    eyeTurn,
    visionOtherEnabled,
    visionOther,
    communication,
    lastSlept,
  ]);

  const renderResult = () => {
    if (!result) return null;
    const targetPath = result.id ? `/results/${result.id}` : null;
    const hasScore =
      typeof result.label === 'string' &&
      result.label.trim().length > 0 &&
      typeof result.confidence === 'number' &&
      Number.isFinite(result.confidence);
    return (
      <>
        <Typography variant="h6" sx={{ mt: 3 }}>
          结果
        </Typography>
        {hasScore ? (
          <>
            <Typography>结论：{formatScreeningLabel(result.label).text}</Typography>
            <Typography>置信度：{Number(result.confidence).toFixed(2)}</Typography>
          </>
        ) : (
          <Typography color="text.secondary">数据已采集（待模型评分）</Typography>
        )}
        {Array.isArray(result.tasks) && result.tasks.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ mt: 1 }}>
              分项结果
            </Typography>
            <ul style={{ marginTop: 4 }}>
              {result.tasks.map((task) => (
                <li key={task.task}>
                  <Typography variant="body2">
                    {task.task}：{formatScreeningLabel(task.label).text}（
                    {task.confidence.toFixed(2)}）
                    {typeof task.prob === 'number' ? ` • 风险 ${task.prob.toFixed(2)}` : ''}
                  </Typography>
                </li>
              ))}
            </ul>
          </>
        )}
        <Box display="flex" gap={2} sx={{ mt: 2 }}>
          {targetPath ? (
            <Button variant="contained" onClick={() => nav(targetPath, { replace: true })}>
              查看详情
            </Button>
          ) : (
            <Button variant="contained" onClick={() => nav('/dashboard', { replace: true })}>
              返回控制台
            </Button>
          )}
          <Button variant="outlined" onClick={() => setResult(null)}>
            隐藏结果
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        height: '100%',
        minHeight: 0,
        py: { xs: 2, sm: 2.5 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader title="家长问卷" subtitle="开始眼动采集前填写家长问卷，完成后自动返回控制台" />
      <Card sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
        <CardContent
          sx={{
            p: { xs: 2.5, sm: 3 },
            height: '100%',
            overflow: 'auto',
            '&:last-child': { pb: { xs: 2.5, sm: 3 } },
          }}
        >
          <Dialog
            open={dobPickerOpen}
            onClose={() => setDobPickerOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>选择出生日期</DialogTitle>
            <DialogContent dividers>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="zh-cn">
                <DateCalendar
                  value={dobPickerValue}
                  onChange={(next) => {
                    if (!next || !next.isValid()) return;
                    setDob(next.format('YYYY-MM-DD'));
                    setDobPickerOpen(false);
                  }}
                  disableFuture
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDobPickerOpen(false)}>取消</Button>
            </DialogActions>
          </Dialog>

          <Stepper
            activeStep={status === 'opened' ? 1 : status === 'done' ? 2 : 0}
            alternativeLabel
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: alpha(productTokens.iris, 0.035),
              border: `1px solid ${alpha(productTokens.iris, 0.12)}`,
            }}
          >
            <Step>
              <StepLabel>家长问卷</StepLabel>
            </Step>
            <Step>
              <StepLabel>眼动采集</StepLabel>
            </Step>
            <Step>
              <StepLabel>完成</StepLabel>
            </Step>
          </Stepper>

          <Typography variant="h6" color="secondary.dark" sx={{ mb: 2 }}>
            家长问卷
          </Typography>

          <Stack spacing={2.25} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                1. 儿童标识
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="院内儿童编号"
                  value={childClinicId}
                  onChange={(event) => setChildClinicId(event.target.value.trimStart())}
                  required
                  fullWidth
                  error={Boolean(fieldErrors.childClinicId)}
                  helperText={fieldErrors.childClinicId ?? '使用院内现有 patient/participant ID'}
                />
                <TextField
                  label="儿童姓名（可选）"
                  value={childName}
                  onChange={(event) => setChildName(event.target.value)}
                  fullWidth
                />
              </Stack>
            </Box>

            <Divider />

            <FormControl
              required
              error={Boolean(fieldErrors.officialDiagnosisStatus)}
              component="fieldset"
            >
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                2. 参考 ASD 状态
              </Typography>
              <RadioGroup
                value={officialDiagnosisStatus}
                onChange={(event) =>
                  setOfficialDiagnosisStatus(event.target.value as IrisDiagnosisStatus)
                }
              >
                <FormControlLabel value="asd" control={<Radio />} label="已确认 ASD" />
                <FormControlLabel value="non_asd" control={<Radio />} label="已确认非 ASD" />
                <FormControlLabel value="suspected_asd" control={<Radio />} label="疑似 / 评估中" />
                <FormControlLabel
                  value="not_yet_confirmed"
                  control={<Radio />}
                  label="暂未确认"
                />
              </RadioGroup>
              {fieldErrors.officialDiagnosisStatus ? (
                <FormHelperText>{fieldErrors.officialDiagnosisStatus}</FormHelperText>
              ) : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
                <TextField
                  label="确认日期（可选）"
                  value={diagnosedOn}
                  onChange={(event) => setDiagnosedOn(normalizeDobInput(event.target.value))}
                  placeholder="YYYY-MM-DD"
                  fullWidth
                />
                <TextField
                  label="记录人员（可选）"
                  value={clinicianName}
                  onChange={(event) => setClinicianName(event.target.value)}
                  fullWidth
                />
                <TextField
                  label="来源机构（可选）"
                  value={diagnosisInstitution}
                  onChange={(event) => setDiagnosisInstitution(event.target.value)}
                  fullWidth
                />
              </Stack>
            </FormControl>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                3. 儿童年龄
              </Typography>
              <TextField
                label="出生日期"
                value={dob}
                onChange={(event) => setDob(normalizeDobInput(event.target.value))}
                onBlur={() => {
                  const parts = parseDob(dob);
                  if (!parts) return;
                  setDob(
                    `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`,
                  );
                }}
                required
                fullWidth
                placeholder="YYYY-MM-DD"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        aria-label="打开日历选择出生日期"
                        edge="end"
                        size="small"
                        onClick={() => setDobPickerOpen(true)}
                      >
                        <CalendarMonthIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '\\d{4}-\\d{2}-\\d{2}',
                  maxLength: 10,
                }}
                error={Boolean(fieldErrors.dob)}
                helperText={fieldErrors.dob ?? '格式：YYYY-MM-DD（系统自动计算月龄）'}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                月龄：{ageMonths === null ? '—' : `${ageMonths} 个月`}
              </Typography>
            </Box>

            <Divider />

            <FormControl required error={Boolean(fieldErrors.bornEarly)} component="fieldset">
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                4. 孩子是否早产？
              </Typography>
              <RadioGroup
                value={bornEarly}
                onChange={(event) => setBornEarly(event.target.value as IrisBornEarlyAnswer)}
              >
                <FormControlLabel value="no" control={<Radio />} label="否" />
                <FormControlLabel value="yes" control={<Radio />} label="是" />
                <FormControlLabel value="unsure" control={<Radio />} label="不确定" />
              </RadioGroup>
              {bornEarly === 'yes' ? (
                <TextField
                  label="早产多少周？"
                  type="number"
                  value={weeksEarly}
                  onChange={(event) =>
                    setWeeksEarly(event.target.value === '' ? '' : Number(event.target.value))
                  }
                  inputProps={{ min: 1, max: 40 }}
                  error={Boolean(fieldErrors.weeksEarly)}
                  helperText={fieldErrors.weeksEarly ?? ''}
                />
              ) : null}
              {fieldErrors.bornEarly ? (
                <FormHelperText>{fieldErrors.bornEarly}</FormHelperText>
              ) : null}
            </FormControl>

            <Divider />

            <FormControl required error={Boolean(fieldErrors.visionConcerns)} component="fieldset">
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                5. 是否有视力或眼部情况方面的担忧？
              </Typography>
              <RadioGroup
                value={visionConcerns}
                onChange={(event) =>
                  setVisionConcerns(event.target.value as IrisVisionConcernAnswer)
                }
              >
                <FormControlLabel value="no" control={<Radio />} label="否" />
                <FormControlLabel value="yes" control={<Radio />} label="是" />
                <FormControlLabel value="unsure" control={<Radio />} label="不确定" />
              </RadioGroup>
              {fieldErrors.visionConcerns ? (
                <FormHelperText>{fieldErrors.visionConcerns}</FormHelperText>
              ) : null}

              {visionConcerns === 'yes' ? (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    若是：
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={wearsGlasses}
                          onChange={(event) => setWearsGlasses(event.target.checked)}
                        />
                      }
                      label="戴眼镜"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={eyeTurn}
                          onChange={(event) => setEyeTurn(event.target.checked)}
                        />
                      }
                      label="眼位偏斜"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={visionOtherEnabled}
                          onChange={(event) => setVisionOtherEnabled(event.target.checked)}
                        />
                      }
                      label="其他"
                    />
                  </FormGroup>
                  {visionOtherEnabled ? (
                    <TextField
                      label="其他（可选）"
                      value={visionOther}
                      onChange={(event) => setVisionOther(event.target.value)}
                      fullWidth
                      sx={{ mt: 1 }}
                    />
                  ) : null}
                </Box>
              ) : null}
            </FormControl>

            <Divider />

            <FormControl required error={Boolean(fieldErrors.communication)} component="fieldset">
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                6. 孩子通常如何沟通？
              </Typography>
              <RadioGroup
                value={communication}
                onChange={(event) => setCommunication(event.target.value as IrisCommunicationLevel)}
              >
                <FormControlLabel value="no_words" control={<Radio />} label="尚未使用语言" />
                <FormControlLabel value="few_single_words" control={<Radio />} label="少量单词" />
                <FormControlLabel value="many_single_words" control={<Radio />} label="较多单词" />
                <FormControlLabel
                  value="two_word_phrases_or_more"
                  control={<Radio />}
                  label="两词短语或更多"
                />
              </RadioGroup>
              {fieldErrors.communication ? (
                <FormHelperText>{fieldErrors.communication}</FormHelperText>
              ) : null}
            </FormControl>

            <Divider />

            <FormControl required error={Boolean(fieldErrors.lastSlept)} component="fieldset">
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 1 }}>
                7. 孩子上一次睡觉是在：
              </Typography>
              <RadioGroup
                value={lastSlept}
                onChange={(event) => setLastSlept(event.target.value as IrisLastSlept)}
              >
                <FormControlLabel value="lt2h" control={<Radio />} label="2 小时以内" />
                <FormControlLabel value="2to6h" control={<Radio />} label="2–6 小时" />
                <FormControlLabel value="gt6h" control={<Radio />} label="6 小时以上" />
              </RadioGroup>
              {fieldErrors.lastSlept ? (
                <FormHelperText>{fieldErrors.lastSlept}</FormHelperText>
              ) : null}
            </FormControl>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="body2">
                测试约 90 秒，会保存完整摄像头视频、刺激时间线和问卷元数据，用于后续离线建模。
              </Typography>
              <Typography variant="body2">
                浏览器可能会拦截弹窗：如无法打开，请在地址栏允许弹出窗口后重试。
              </Typography>
            </Stack>
          </Alert>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
            <Button variant="contained" color="secondary" size="large" onClick={openRunner}>
              打开测试窗口
            </Button>
            {status === 'opened' && (
              <Button variant="outlined" onClick={() => winRef.current?.focus()}>
                置于前台
              </Button>
            )}
          </Stack>
          {status === 'done' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              测试已完成——请查看下方结果。
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mt: 3, color: 'text.secondary' }}>
            无法打开窗口？{' '}
            <Link href="/runner/start" target="_blank" rel="noreferrer" underline="hover">
              点此直接打开测试页
            </Link>
            。
          </Typography>
          {renderResult()}
        </CardContent>
      </Card>
    </Container>
  );
}
